import React, { useMemo, useRef, useState } from "react";
import {
  Card,
  Col,
  Row,
  Form,
  Input,
  Button,
  Space,
  ColorPicker,
  Avatar,
  Tour,
  InputNumber,
  Flex,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";

import MainLayout from "components/MainLayout";

import useGet from "hooks/useGet";
import useUpdate from "hooks/useUpdate";
import useDelete from "hooks/useDelete";

import PageFailure from "components/PageFailure";
import { MaskedInput } from "antd-mask-input";
import Logs from "components/Logs";
import useFormErrors from "hooks/useFormErrors";
import { name_regexp } from "helpers/regexp";
import ModalDelete from "components/ModalDelete";
import WorkerDevices from "./components/WorkerDevices";
import WorkerPasswordResets from "./components/WorkerPasswordResets";
import WorkerPermissions from "./components/WorkerPermissions";
import WorkerStatus from "./components/WorkerStatus";

const WorkerUpdate = () => {
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const [open_tour, setOpenTour] = useState(false);

  const steps = [
    {
      title: "Krok 1 - Aktywuj pracownika",
      description: "Kliknij, aby oznaczyć pracownika jako aktywny.",
      target: () => ref1.current,
      nextButtonProps: { children: "Dalej" },
    },
    {
      title: "Krok 2 - Aktywuj urządzenie pracownika",
      description: "Kliknij, aby odblokować urządzenie pracownikowi.",
      target: () => ref2.current,
      prevButtonProps: { children: "Wróć" },
      nextButtonProps: { children: "Zakończ" },
    },
  ];

  const { id } = useParams();
  const navigate = useNavigate();

  const path = `/workers/${id}`;

  const [color, setColor] = useState(null);

  const bgColor = useMemo(() => color, [color]);

  const { is_loading, is_error, data, refetch } = useGet({
    path,
    query: ["workers", id],
    onSuccess: (res) => setColor(res.data?.color),
  });

  const [form] = Form.useForm();

  const { is_saving, update } = useUpdate({
    path,
    form,
    onSuccess: refetch,
  });

  const { is_deleting, _delete } = useDelete({
    path,
    onSuccess: () => navigate("/workers"),
  });

  const { pristine, has_error, onFieldsChange } = useFormErrors(data);

  if (is_error) {
    return (
      <PageFailure
        title="Nie znaleziono pracownika."
        button_text="Wróć do listy"
        path="/workers"
      />
    );
  }

  return (
    <MainLayout
      back_href="/workers"
      breadcrumbs={[
        { name: "Pracownicy", path: "/workers" },
        { name: data?.id || "Trwa pobieranie..." },
      ]}
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} md={24} lg={24} xl={8}>
          <Card title="Edytuj pracownika" loading={is_loading}>
            <Form
              form={form}
              layout="vertical"
              onFieldsChange={onFieldsChange}
              initialValues={{
                first_name: data?.first_name,
                last_name: data?.last_name,
                email: data?.email,
                phone: data?.phone,
                color: data?.color,
                position: data?.position,
              }}
              onFinish={(values) => update(values)}
            >
              {(values) => (
                <>
                  <Form.Item
                    label="Imię"
                    name="first_name"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Imię jest wymagane!",
                      },
                      {
                        pattern: name_regexp,
                        message: "Pole zawiera niedowolone znaki!",
                      },
                    ]}
                  >
                    <Input placeholder="Imię" maxLength={60} />
                  </Form.Item>
                  <Form.Item
                    label="Nazwisko"
                    name="last_name"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Nazwisko jest wymagane!",
                      },
                      {
                        pattern: name_regexp,
                        message: "Pole zawiera niedowolone znaki!",
                      },
                    ]}
                  >
                    <Input placeholder="Nazwisko" maxLength={60} />
                  </Form.Item>
                  <Form.Item
                    label="E-mail"
                    name="email"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        type: "email",
                        message: "E-mail jest niepoprawny!",
                      },
                    ]}
                  >
                    <Input placeholder="E-mail" maxLength={50} />
                  </Form.Item>
                  <Form.Item
                    required
                    hasFeedback
                    label="Telefon"
                    name="phone"
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(
                              new Error("Numer telefonu wymagany!")
                            );
                          }
                          if (value.includes("_")) {
                            return Promise.reject(
                              new Error("Numer telefonu niepoprawny!")
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <MaskedInput mask="000000000" placeholder="Telefon" />
                  </Form.Item>
                  <Form.Item
                    label="Pozycja"
                    name="position"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Pozycja jest wymagana!",
                      },
                    ]}
                  >
                    <InputNumber min={1} placeholder="Pozycja" type="number" />
                  </Form.Item>
                  <Form.Item name="color">
                    <ColorPicker
                      value={data?.color}
                      onChange={(value) => setColor(value.toHexString())}
                      onChangeComplete={(value) =>
                        form.setFieldValue("color", value.toHexString())
                      }
                    >
                      <Space>
                        <Avatar
                          style={{
                            cursor: "pointer",
                            backgroundColor: bgColor,
                          }}
                        >
                          {values.first_name?.charAt(0)?.toUpperCase()}
                          {values.last_name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Button
                          type="primary"
                          style={{ backgroundColor: bgColor }}
                        >
                          Zmień kolor
                        </Button>
                      </Space>
                    </ColorPicker>
                  </Form.Item>
                  <Row justify="end">
                    <Space>
                      <ModalDelete
                        loading={is_deleting}
                        text="pracownika"
                        handleDelete={_delete}
                      />
                      <Button
                        disabled={pristine || has_error}
                        loading={is_deleting || is_loading || is_saving}
                        type="primary"
                        htmlType="submit"
                      >
                        Zapisz
                      </Button>
                    </Space>
                  </Row>
                </>
              )}
            </Form>
          </Card>
          <WorkerPermissions data={data} update={update} />
          <WorkerPasswordResets
            data={data?.worker_password_resets}
            refetch={refetch}
          />
        </Col>
        <Col xs={24} md={24} lg={24} xl={16}>
          <Flex justify="center" style={{ marginBottom: 30 }}>
            <Button type="primary" onClick={() => setOpenTour(true)}>
              Rozpocznij aktywację
            </Button>
          </Flex>
          <WorkerStatus
            updateUser={update}
            ref1={ref1}
            is_worker_active={data?.is_active}
            active_device={data?.worker_devices.find(
              ({ is_active }) => is_active
            )}
          />
          <WorkerDevices
            data={data?.worker_devices}
            refetch={refetch}
            ref2={ref2}
          />

          <Logs
            title="Logi pracownika"
            loading={is_loading}
            styles={{
              body: {
                maxHeight: 825,
                overflowY: "auto",
              },
            }}
            data={data?.logs || []}
          />
        </Col>
      </Row>
      <Tour
        open={open_tour}
        onClose={() => setOpenTour(false)}
        steps={steps}
        indicatorsRender={(current, total) => (
          <span>
            {current + 1} / {total}
          </span>
        )}
      />
    </MainLayout>
  );
};

export default WorkerUpdate;
